import { NavLink } from 'react-router-dom'
import DataTable from '../../../common/data-table/DataTable'
import PageHeader from '../../../layout/page/PageHeader'
import dayjs from 'dayjs'
import { FilterMatchMode } from 'primereact/api'
import { Calendar } from 'primereact/calendar'
import { Alert, ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import TrueFalseCell from '../../../common/data-table/TrueFalseCell'
import { useAppSelector } from '../../../../store/hooks'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

type TableRow = Record<string, unknown> & { id?: number; name?: string; created_at?: string | null }
type DateFilterOptions = { value: unknown; filterCallback: (v: unknown, i: number) => void; index: number }
type StatusLabels = { trueLabel: string; falseLabel: string }

const Languages = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const [refreshTable, setRefreshTable] = useState(false)

    const dateTimeBodyTemplate = (rowData: TableRow) => {
        return rowData.created_at != null
            ? dayjs(rowData.created_at as string).format('DD-MMM-YYYY @ HH:mm:ss')
            : ''
    }

    const dateFilterTemplate = (options: DateFilterOptions) => {
        return (
            <Calendar
                value={options.value as Date | null | undefined}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="dd/mm/yy"
                placeholder="dd/mm/yyyy"
                mask="99/99/9999"
            />
        )
    }

    const actionsBodyTemplate = (rowData: TableRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/administration/languages/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete page:
                                <strong> {rowData.name}</strong>?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All codes related to
                                this language will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
                <NavLink
                    to={'/administration/languages/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData: TableRow, field: string, labels: StatusLabels) => {
        return (
            <TrueFalseCell
                value={rowData[field] === 1}
                trueLabel={labels.trueLabel}
                falseLabel={labels.falseLabel}
            />
        )
    }

    return (
        <>
            <PageHeader
                icon="globe"
                subtitle="Add and manage supported languages"
                gradient="linear-gradient(135deg, #10b981, #059669)"
                header="Languages"
                buttons={[
                    {
                        variant: theme === 'dark' ? 'dark' : 'light',
                        label: 'Back to Administration',
                        icon: 'fa-solid fa-arrow-left',
                        url: '/administration',
                    },
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'fa-solid fa-plus',
                        url: '/administration/languages/new',
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                config={{
                    dataRoute: '/administration/languages',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'name',
                    sortOrder: 1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        locale_name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        created_at: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    // {
                    //     field: 'select-fields',
                    //     header: 'Select',
                    //     selectionMode: 'multiple',
                    //     headerStyle: { width: '3rem' },
                    //     excludeFromShowHide: true
                    // },
                    // {
                    //     field: 'code',
                    //     header: 'code',
                    //     sortable: true,
                    //     filter: true,
                    //     filterPlaceholder: 'Search',
                    // },
                    {
                        field: 'name',
                        header: 'Name',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                    },
                    {
                        field: 'locale_name',
                        header: 'Locale',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '8.5rem',
                            minWidth: '8.5rem',
                            maxWidth: '8.5rem',
                        },
                        style: {
                            width: '8.5rem',
                            minWidth: '8.5rem',
                            maxWidth: '8.5rem',
                        },
                    },
                    {
                        field: 'active',
                        header: 'Active',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'active', {
                                trueLabel: 'Active',
                                falseLabel: 'Inactive',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'available',
                        header: 'Available',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'available', {
                                trueLabel: 'Available on web',
                                falseLabel: 'Not available for the web',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'default',
                        header: 'Default',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'default', {
                                trueLabel: 'Default',
                                falseLabel: 'Not default',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    // {
                    //     field: 'created_at',
                    //     header: 'Date Created',
                    //     sortable: true,
                    //     filter: true,
                    //     filterPlaceholder: 'Search',
                    //     body: dateTimeBodyTemplate,
                    // },
                    {
                        field: 'created_at',
                        header: 'Created At',
                        sortable: true,
                        filter: true,
                        dataType: 'date',
                        body: dateTimeBodyTemplate,
                        filterElement: dateFilterTemplate,
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '15rem',
                            minWidth: '15rem',
                            maxWidth: '15rem',
                        },
                        style: {
                            width: '15rem',
                            minWidth: '15rem',
                            maxWidth: '15rem',
                        },
                    },
                    // {
                    //     field: 'representative.name',
                    //     header: 'Representative',
                    //     sortable: true,
                    //     filter: true,
                    //     filterPlaceholder: 'Search',
                    //     body: representativeBodyTemplate,
                    // },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={['name', 'locale_name', 'created_at']}
            />
        </>
    )
}

export default Languages
